import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  Shield,
  Save,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface MRAConfig {
  terminalId: string;
  apiKey: string;
  baseUrl: string;
  isActive: boolean;
  lastSyncAt?: string;
}

interface SystemConfig {
  businessName: string;
  businessAddress: string;
  businessPhone: string;
  businessEmail: string;
  businessTPIN: string;
  taxRate: number;
  currency: string;
  timezone: string;
}

const ConfigPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [mraFormData, setMraFormData] = useState({
    terminalId: '',
    apiKey: '',
    apiSecret: '',
    baseUrl: '',
    isActive: true,
  });

  const { data: mraConfig, isLoading: isLoadingMRA } = useQuery<MRAConfig | null>({
    queryKey: ['config', 'mra'],
    queryFn: async () => {
      const res = await api.get('/api/config/mra');
      return res.data?.data?.config || res.data?.config || null;
    },
  });

  useEffect(() => {
    if (mraConfig) {
      setMraFormData({
        terminalId: mraConfig.terminalId || '',
        apiKey: mraConfig.apiKey || '',
        apiSecret: '',
        baseUrl: mraConfig.baseUrl || '',
        isActive: mraConfig.isActive ?? true,
      });
    }
  }, [mraConfig]);

  const { data: systemConfig, isLoading: isLoadingSystem } = useQuery<SystemConfig>({
    queryKey: ['config', 'system'],
    queryFn: async () => {
      const res = await api.get('/api/config/system');
      return res.data?.data?.config || res.data?.config;
    },
  });

  const updateMRAMutation = useMutation({
    mutationFn: async (payload: typeof mraFormData) => {
      const res = await api.put('/api/config/mra', payload);
      return res.data?.data?.config || res.data?.config;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'mra'] });
      toast.success('MRA configuration updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update MRA configuration');
    },
  });

  const handleMRAUpdate = () => {
    updateMRAMutation.mutate(mraFormData);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="mt-1 text-sm text-gray-600">Configure system and MRA integration</p>
        </div>
      </div>

      {/* System Configuration */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="card"
      >
        <div className="card-header">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            System Configuration
          </h2>
        </div>
        <div className="card-body">
          {isLoadingSystem ? (
            <div className="text-sm text-gray-600">Loading system configuration...</div>
          ) : systemConfig ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Business Name</label>
                <p className="mt-1 text-sm text-gray-900">{systemConfig.businessName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Business Address</label>
                <p className="mt-1 text-sm text-gray-900">{systemConfig.businessAddress}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Business Phone</label>
                <p className="mt-1 text-sm text-gray-900">{systemConfig.businessPhone}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Business Email</label>
                <p className="mt-1 text-sm text-gray-900">{systemConfig.businessEmail}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">TPIN</label>
                <p className="mt-1 text-sm text-gray-900">{systemConfig.businessTPIN}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Tax Rate</label>
                <p className="mt-1 text-sm text-gray-900">{systemConfig.taxRate}%</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Currency</label>
                <p className="mt-1 text-sm text-gray-900">{systemConfig.currency}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Timezone</label>
                <p className="mt-1 text-sm text-gray-900">{systemConfig.timezone}</p>
              </div>
            </div>
          ) : (
            <div className="text-sm text-red-600">Failed to load system configuration</div>
          )}
        </div>
      </motion.div>

      {/* MRA Configuration */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="card"
      >
        <div className="card-header">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            MRA Integration Configuration
          </h2>
          <div className="flex items-center mt-2">
            {mraConfig?.isActive ? (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3 mr-1" />
                Active
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                <AlertCircle className="h-3 w-3 mr-1" />
                Inactive
              </span>
            )}
          </div>
        </div>
        <div className="card-body">
          {isLoadingMRA ? (
            <div className="text-sm text-gray-600">Loading MRA configuration...</div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleMRAUpdate(); }} className="space-y-4">
              <Input
                label="Terminal ID"
                value={mraFormData.terminalId}
                onChange={(e) => setMraFormData({ ...mraFormData, terminalId: e.target.value })}
                placeholder="MRA Terminal ID"
                required
              />
              <Input
                label="API Key"
                value={mraFormData.apiKey}
                onChange={(e) => setMraFormData({ ...mraFormData, apiKey: e.target.value })}
                placeholder="MRA API Key"
                required
              />
              <Input
                label="API Secret"
                type="password"
                value={mraFormData.apiSecret}
                onChange={(e) => setMraFormData({ ...mraFormData, apiSecret: e.target.value })}
                placeholder="Enter new API secret to update"
              />
              <Input
                label="Base URL"
                value={mraFormData.baseUrl}
                onChange={(e) => setMraFormData({ ...mraFormData, baseUrl: e.target.value })}
                placeholder="https://api.mra.mw"
                required
              />
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={mraFormData.isActive}
                  onChange={(e) => setMraFormData({ ...mraFormData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Enable MRA Integration</span>
              </label>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={updateMRAMutation.isPending}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {updateMRAMutation.isPending ? 'Saving...' : 'Save Configuration'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ConfigPage;
